import {
  DEFAULT_LANG,
  isSupportedLanguage,
  Language,
  toSupportedLangOrUndefined,
} from "./languages";
import { logDebug, logError } from "../utils/log";
import { createLocaleFormat, LocaleFormat } from "./localeFormat";
import { DCEvent, EventDispatcher } from "../events/eventsDomain";
import {
  isTMessage,
  isTMessageParam,
  isTranslatedMessage,
  TFunction,
  TMessage,
  TMessageParam,
  TMessageParams,
} from "./tt";
import { TMessageKey } from "./types.gen";
import en from "./locales/en/translation.json";
import no from "./locales/no/translation.json";
import sv from "./locales/sv/translation.json";
import { updateUserPrefEvent, UserPrefs } from "../stores/userPrefsDomain";
import { fromQueryString, QueryParams } from "../utils/queryParams";
import { capitalize, identity, isNil, omit } from "lodash";
import { uncapitalize } from "../utils/strings";

type I18nBundle = { [key: string]: string };

export type I18nState = {
  bundles: { [lang: string]: I18nBundle };
  lang: Language;
  cimode?: boolean;
};

export type PreppedI18nState = {
  lang: Language;
  t: TFunction;
  f: LocaleFormat;
  tf: (val: TMessage | TMessageParam | string | number | undefined | null) => string;
};

export const lookupLanguage = ({
  queryParams,
  userPrefs,
  browserLangs,
}: {
  queryParams: QueryParams | undefined;
  userPrefs: UserPrefs | undefined;
  browserLangs: string[] | undefined;
}): Language => {
  const browserLang = browserLangs?.map(toSupportedLangOrUndefined)?.find(identity);
  const storedLang = userPrefs?.lang;
  const queryLang = queryParams?.lang ? toSupportedLangOrUndefined(queryParams.lang) : undefined;
  return queryLang ?? storedLang ?? browserLang ?? DEFAULT_LANG;
};

export function mapMessageParam<Key extends string = string>(
  state: I18nState,
  f: LocaleFormat,
  v: TMessageParams[Key],
): string | number {
  if (isNil(v)) {
    return "";
  }
  if (Array.isArray(v)) {
    return f.formatList(v.map((vv) => mapMessageParam(state, f, vv)));
  }
  switch (typeof v) {
    case "string":
    case "number":
      return v;
    case "object":
      if (isTMessage(v)) {
        return t(state, v);
      } else {
        switch (v.type) {
          case "capitalize":
            if (typeof v.value === "string") {
              return capitalize(v.value);
            } else if (isTMessage(v.value)) {
              return capitalize(t(state, v.value));
            } else {
              return capitalize(String(mapMessageParam(state, f, v.value)));
            }
          case "date":
            return f.formatDate(v.value, v);
          case "datetime":
            return f.formatDateTime(v.value, v);
          case "time":
            return f.formatTime(v.value, v);
          case "weekday":
            return f.formatWeekday(v.value);
          case "number":
            return f.formatNumber(v.value, v.maximumFactionDigits);
          case "price":
            return f.formatPrice(v.currency, v.value);
          case "amount":
            if (!v.currency) {
              return f.formatAmountNumber(v.value);
            } else {
              return f.formatAmount(v.currency, v.value);
            }
          case "exactAmount":
            return f.formatExactAmount({ currency: v.currency, value: v.value });
          case "percent":
            return f.formatPercent(v.value);
          case "nominal-value":
            return f.formatNominalValue(v.currency, v.value);
          case "list":
            return f.formatList(
              v.value.map((vv) => mapMessageParam(state, f, vv)),
              omit(v, ["type", "value"]),
            );
          case "md-list":
            return v.value
              .map((vv) => mapMessageParam(state, f, vv))
              .map((s) => `- ${s}\n`)
              .join("");
          case "country":
            return f.formatCountry(v.code);
          case "ssn":
            return f.formatSsn(v.value, v.options);
          case "uncapitilize":
            if (isTMessage(v.value)) {
              return uncapitalize(String(t(state, v.value)));
            } else if (isTMessageParam(v.value)) {
              return uncapitalize(String(mapMessageParam(state, f, v.value)));
            } else {
              return uncapitalize(v.value);
            }
          case "year":
            return f.formatYear(v.value);
          case "year-month":
            return f.formatYearMonth(v.value);
        }
      }
  }
}

const lookupMessage = (
  state: I18nState,
  lang: Language,
  key: string,
  params?: TMessageParams,
): string | undefined => {
  if (state.cimode) {
    return key;
  }
  const defaultBundle = state.bundles[DEFAULT_LANG];
  const bundle = state.bundles[lang] ?? defaultBundle;
  const msg = bundle[key] ?? defaultBundle[key];
  if (msg) {
    return msg;
  } else if (typeof params?.count === "number") {
    const k = params.count === 1 ? `${key}_one` : `${key}_other`;
    return bundle[k] ?? defaultBundle[k];
  }
  return undefined;
};

export const t = (
  state: I18nState,
  key: TMessageKey | TMessage | TMessageParam,
  params?: TMessageParams,
): string => {
  const lang = isSupportedLanguage(params?.lang) ? params.lang : state.lang;
  const defaultBundle = state.bundles[DEFAULT_LANG];
  const bundle = state.bundles[lang] ?? defaultBundle;
  const f = createLocaleFormat(lang);
  if (isTranslatedMessage(key)) {
    return key.translated;
  }
  if (isTMessageParam(key)) {
    return String(mapMessageParam(state, f, key));
  }

  let k: string;
  let p: TMessageParams | undefined;
  if (typeof key === "string") {
    k = key;
    p = params;
  } else {
    k = key.key;
    p = key.params;
  }
  if (!bundle) {
    return k;
  }

  const msg = lookupMessage(state, lang, k, p);
  if (msg && !p) {
    return msg;
  } else if (msg && p) {
    const formattedParams = Object.entries(p).map(([k, v]) => [k, mapMessageParam(state, f, v)]);
    return formattedParams.reduce((acc, [k, v]) => acc.replaceAll(`{{${k}}}`, String(v)), msg);
  } else {
    logError(`Missing translation "${k}" for language "${lang}"`, { lang, key, params });
    return k;
  }
};

export const initialI18nState = (): I18nState => ({
  bundles: { en, no, sv },
  lang: DEFAULT_LANG,
});

export const initI18n = ({
  dispatch,
  userPrefs,
}: {
  dispatch: EventDispatcher;
  userPrefs: UserPrefs;
}) => {
  dispatch([
    {
      type: "update-store",
      data: {
        i18n: {
          bundles: { en, no, sv },
          lang: lookupLanguage({
            queryParams: fromQueryString(location.search),
            browserLangs: Array.from(navigator.languages),
            userPrefs,
          }),
          cimode: fromQueryString(location.search)?.lang === "cimode",
        },
      },
    },
  ]);

  if (import.meta.hot) {
    import.meta.hot.on(
      "i18n/hot-update",
      ({ lang, content }: { lang: string; content: object }) => {
        logDebug(`i18n: hot-update ${lang}`);
        dispatch([
          {
            type: "update-store",
            data: {
              i18n: (cur) => ({
                ...cur,
                bundles: { ...cur.bundles, [lang]: content as I18nBundle },
              }),
            },
          },
        ]);
      },
    );
  }
};

export const changeLanguageAction = (lang: Language): DCEvent[] => [
  { type: "update-store", data: { i18n: (state) => ({ ...state, lang }) } },
  ...updateUserPrefEvent("lang", lang),
];
